import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Instagram, Share2, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";
import { footer } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_PATH = "/feral-fest-2027";
const OG_IMAGE = "https://feralcollective.co.uk/hero-laughing.jpg";

const INTERESTS = [
  "Full weekend",
  "One-day ticket",
  "Accommodation",
  "Premium/VIP experience",
  "Volunteering",
  "Trading or facilitating",
  "Not sure yet",
];

const BENEFITS = [
  "The festival announcement before everyone else",
  "Priority access to the first ticket release",
  "The lowest available launch price",
  "First access to accommodation and premium experiences",
  "Selected behind-the-scenes updates as the next FERAL takes shape",
];

const readUtm = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
    const v = p.get(k);
    if (v) utm[k] = v;
  });
  return utm;
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const FeralFest2027 = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    interests: [],
    attended2026: "",
    message: "",
    lovedMost: "",
    doBetter: "",
    moreOf: "",
    consent: false,
    website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // "success" | "exists"
  const utmRef = useRef(readUtm());
  const startedRef = useRef(false);
  const formRef = useRef(null);

  const track = useCallback((event, meta) => {
    axios
      .post(`${API}/track`, { event, page: PAGE_PATH, utm: utmRef.current, meta: meta || {} })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "FERAL Fest Spring 2027 | Early Access";
    const desc =
      "Join the FERAL Fest Spring 2027 early-access list for priority tickets, launch pricing and the first festival announcements.";
    setMeta("name", "description", desc);
    setMeta("property", "og:title", "FERAL Fest Spring 2027 | Early Access");
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("property", "og:type", "website");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", OG_IMAGE);
    track("page_view");
    return () => {
      document.title = prevTitle;
    };
  }, [track]);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("form_start");
    }
  };

  const update = (k, v) => {
    markStarted();
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleInterest = (opt) => {
    markStarted();
    setForm((f) => {
      const has = f.interests.includes(opt);
      return { ...f, interests: has ? f.interests.filter((x) => x !== opt) : [...f.interests, opt] };
    });
    setErrors((e) => ({ ...e, interests: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Please enter your first name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (form.interests.length === 0) e.interests = "Please choose at least one.";
    if (!form.consent) e.consent = "Please agree to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/early-access`, {
        ...form,
        utm: utmRef.current,
      });
      const status = res.data?.status;
      if (status === "exists") {
        setDone("exists");
      } else {
        track("submit_success");
        setDone("success");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again in a moment.";
      toast({ title: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    track("cta_click", { cta: "join_early_access" });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shareUrl = "https://feralcollective.co.uk" + PAGE_PATH + "?utm_source=share&utm_medium=referral";
  const handleShare = async () => {
    track("cta_click", { cta: "share" });
    const shareData = {
      title: "FERAL Fest Spring 2027",
      text: "Know someone who belongs at FERAL? Invite them to join you.",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: "Link copied", description: "Share it with someone who belongs at FERAL." });
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  const inputCls =
    "w-full bg-[#f3ece1] text-[#1e231f] placeholder-[#9a876f] px-4 py-4 rounded-lg outline-none text-base focus:ring-2 focus:ring-[#ebc754] focus:ring-offset-2 focus:ring-offset-[#14110f] transition";

  return (
    <div className="bg-[#2c6052] text-[#f3ece1] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <img
          src="/hero-laughing.jpg"
          alt="Friends laughing together in warm light at a FERAL gathering"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052] via-[#2c6052]/70 to-[#2c6052]/30" />
        <div className="relative z-10 max-w-3xl mx-auto w-full px-6 pb-16 md:pb-24">
          <p className="text-[#ebc754] tracking-[0.28em] text-xs sm:text-sm font-semibold uppercase mb-5">
            Spring 2027 · Early Access
          </p>
          <h1 className="feral-serif text-[#ebc754] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            You found your FERAL.
            <br />
            Ready for the next one?
          </h1>
          <p className="text-[#f2e5de]/85 text-base md:text-lg leading-relaxed max-w-xl mb-8">
            FERAL Fest returns in Spring 2027. Join the early-access list to be the first to discover the
            date, location and what we are creating next—and receive priority access to the first ticket
            release.
          </p>
          <button
            type="button"
            onClick={scrollToForm}
            data-testid="hero-join-button"
            className="group inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-9 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors duration-300"
          >
            Join Early Access
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {/* THIS IS ONLY THE BEGINNING */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#ebc754] tracking-[0.24em] text-xs sm:text-sm font-semibold uppercase mb-5">
            This is only the beginning
          </p>
          <p className="feral-serif text-[#ebc754] text-2xl md:text-3xl leading-snug mb-5">
            A new kind of festival is taking shape—bringing together music, movement, powerful
            conversations, adventure and genuine human connection.
          </p>
          <p className="text-[#f2e5de]/75 text-base md:text-lg leading-relaxed mb-10">
            We’re taking everything people loved about FERAL and building something even bolder for
            Spring 2027.
          </p>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-8">
            <p className="text-[#f2e5de]/60 text-xs tracking-[0.18em] uppercase font-semibold mb-5">
              Early-access members receive
            </p>
            <ul className="space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#ebc754] text-[#14110f] flex items-center justify-center">
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <span className="text-[#f2e5de]/90 text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FORM / SUCCESS */}
      <section ref={formRef} id="join" className="scroll-mt-4 pb-20 md:pb-28 px-6">
        <div className="max-w-xl mx-auto">
          {done ? (
            <div
              data-testid="early-access-success"
              className="rounded-2xl border border-[#ebc754]/40 bg-white/[0.04] p-8 md:p-10 text-center"
            >
              <h2 className="feral-serif text-[#ebc754] text-3xl md:text-4xl mb-4">
                {done === "exists" ? "You’re already on the list." : "You’re on the list."}
              </h2>
              <p className="text-[#f2e5de]/80 text-base leading-relaxed mb-2">
                {done === "exists"
                  ? "Good news—you’ve already secured your early access. We’ll be in touch first when FERAL returns."
                  : "You’ll be among the first to know when FERAL returns."}
              </p>
              <p className="text-[#f2e5de]/60 text-sm italic mb-8">Until then—stay wild, stay connected.</p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={footer.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="success-instagram"
                  className="inline-flex items-center justify-center gap-2 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] transition-colors"
                >
                  <Instagram size={17} /> Follow FERAL
                </a>
                <Link
                  to="/"
                  data-testid="success-home"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-[#f2e5de] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
                >
                  FERAL website
                </Link>
              </div>

              <div className="mt-8 pt-7 border-t border-white/10">
                <p className="text-[#f2e5de]/70 text-sm mb-4">
                  Know someone who belongs at FERAL? Invite them to join you.
                </p>
                <button
                  type="button"
                  onClick={handleShare}
                  data-testid="success-share"
                  className="inline-flex items-center gap-2 text-[#ebc754] hover:text-[#e0972f] text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
                >
                  <Share2 size={16} /> Share the list
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="feral-serif text-[#ebc754] text-3xl md:text-4xl mb-2">Join early access</h2>
              <p className="text-[#f2e5de]/70 text-base mb-8">
                Priority tickets, launch pricing and first word when FERAL returns.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                {/* honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#f2e5de] text-sm font-medium mb-2">First name *</label>
                    <input
                      className={inputCls}
                      value={form.firstName}
                      onChange={(e) => update("firstName", e.target.value)}
                      data-testid="input-firstName"
                      autoComplete="given-name"
                    />
                    {errors.firstName && (
                      <p className="text-[#f0a878] text-xs mt-1.5" data-testid="error-firstName">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[#f2e5de] text-sm font-medium mb-2">Last name</label>
                    <input
                      className={inputCls}
                      value={form.lastName}
                      onChange={(e) => update("lastName", e.target.value)}
                      data-testid="input-lastName"
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">Email address *</label>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="input-email"
                    autoComplete="email"
                    inputMode="email"
                  />
                  {errors.email && (
                    <p className="text-[#f0a878] text-xs mt-1.5" data-testid="error-email">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">Mobile number</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.mobile}
                    onChange={(e) => update("mobile", e.target.value)}
                    data-testid="input-mobile"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-3">
                    What are you most interested in? * <span className="text-[#f2e5de]/50 font-normal">(choose any)</span>
                  </label>
                  <div className="flex flex-wrap gap-2.5">
                    {INTERESTS.map((opt) => {
                      const active = form.interests.includes(opt);
                      return (
                        <button
                          type="button"
                          key={opt}
                          onClick={() => toggleInterest(opt)}
                          data-testid={`interest-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                          aria-pressed={active}
                          className={`px-4 py-2.5 text-sm font-medium border transition-colors duration-200 ${
                            active
                              ? "bg-[#ebc754] border-[#ebc754] text-[#14110f]"
                              : "bg-transparent border-white/25 text-[#f2e5de]/85 hover:border-[#ebc754]"
                          }`}
                        >
                          {active && <Check size={14} strokeWidth={3} className="inline mr-1.5 -mt-0.5" />}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {errors.interests && (
                    <p className="text-[#f0a878] text-xs mt-2" data-testid="error-interests">{errors.interests}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-3">
                    Did you attend FERAL Fest September 2026?
                  </label>
                  <div className="flex gap-2.5">
                    {["Yes", "No"].map((opt) => {
                      const active = form.attended2026 === opt;
                      return (
                        <button
                          type="button"
                          key={opt}
                          onClick={() => update("attended2026", opt)}
                          data-testid={`attended-${opt.toLowerCase()}`}
                          aria-pressed={active}
                          className={`px-8 py-2.5 text-sm font-medium border transition-colors duration-200 ${
                            active
                              ? "bg-[#ebc754] border-[#ebc754] text-[#14110f]"
                              : "bg-transparent border-white/25 text-[#f2e5de]/85 hover:border-[#ebc754]"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">
                    What did you love most—or what would you love to experience at the next FERAL?
                  </label>
                  <textarea
                    rows={4}
                    className={inputCls + " resize-none"}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="input-message"
                  />
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">
                    What did you love most about FERAL Fest? What made you feel, “this is why I came”?
                  </label>
                  <textarea
                    rows={3}
                    className={inputCls + " resize-none"}
                    value={form.lovedMost}
                    onChange={(e) => update("lovedMost", e.target.value)}
                    data-testid="input-lovedMost"
                  />
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">
                    What could we genuinely do better next time? <span className="text-[#f2e5de]/50 font-normal">Be honest — we want to hear it.</span>
                  </label>
                  <textarea
                    rows={3}
                    className={inputCls + " resize-none"}
                    value={form.doBetter}
                    onChange={(e) => update("doBetter", e.target.value)}
                    data-testid="input-doBetter"
                  />
                </div>

                <div>
                  <label className="block text-[#f2e5de] text-sm font-medium mb-2">
                    What would you love to see more of at the next FERAL Fest? <span className="text-[#f2e5de]/50 font-normal">Experiences, speakers, music, workshops, food, facilities — anything.</span>
                  </label>
                  <textarea
                    rows={3}
                    className={inputCls + " resize-none"}
                    value={form.moreOf}
                    onChange={(e) => update("moreOf", e.target.value)}
                    data-testid="input-moreOf"
                  />
                </div>

                <label className="flex items-start gap-3 text-[#f2e5de]/85 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    data-testid="input-consent"
                    className="mt-0.5 accent-[#ebc754] w-5 h-5 shrink-0"
                  />
                  <span>
                    I agree to receive news and early-access information from FERAL. I can unsubscribe at
                    any time.
                  </span>
                </label>
                {errors.consent && (
                  <p className="text-[#f0a878] text-xs -mt-3" data-testid="error-consent">{errors.consent}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="early-access-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#9b3d0a] hover:bg-[#b5490d] disabled:opacity-60 text-[#f2e5de] px-8 py-4 tracking-[0.16em] text-sm font-bold uppercase transition-colors duration-300"
                >
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Set me free"}
                </button>

                <p className="text-[#f2e5de]/45 text-xs text-center leading-relaxed">
                  Protected against spam. We’ll never add you to unrelated lists.{" "}
                  <a
                    href="mailto:hello@findyourferal.co?subject=FERAL%20privacy%20policy"
                    className="underline hover:text-[#f2e5de]/70"
                    data-testid="privacy-link"
                  >
                    Privacy policy
                  </a>
                  .
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeralFest2027;
